import React, {
  memo, useCallback, useEffect, useState,
} from 'react';

import PropTypes from 'prop-types';
import { TextField } from '@picsio/ui';

import { useLocalFieldValue } from '../hooks';
import ReadOnlyFieldTooltip from '../helpers/ReadOnlyFieldTooltip';

const InventoryTextField = ({
  value,
  label,
  readOnly,
  readOnlyTooltip,
  placeholder,
  normalize,
  validate,
  onCommit,
  inputProps,
}) => {
  const { localValue, setLocalValue } = useLocalFieldValue(value);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLocalValue(value);
    setError(null);
  }, [value, setLocalValue]);

  const handleChange = useCallback((e) => {
    if (readOnly) return;
    setLocalValue(e.target.value);
    if (error) setError(null);
  }, [readOnly, setLocalValue, error]);

  const handleBlur = useCallback(() => {
    if (readOnly) return;
    const trimmed = localValue.trim();
    const normalized = normalize ? normalize(trimmed) : trimmed;
    if (normalized !== localValue) setLocalValue(normalized);

    if (validate) {
      const { value: validatedValue, error: validationError } = validate(normalized);
      if (validationError) {
        setError(validationError);
        return;
      }
      setError(null);
      if (validatedValue !== value) onCommit(validatedValue);
      return;
    }

    setError(null);
    if (normalized !== value) onCommit(normalized);
  }, [readOnly, localValue, value, onCommit, setLocalValue, normalize, validate]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter') e.target.blur();
  }, []);

  return (
    <ReadOnlyFieldTooltip tooltip={readOnly ? readOnlyTooltip : null}>
      <TextField
        value={localValue}
        label={label}
        placeholder={placeholder}
        disabled={readOnly}
        error={Boolean(error)}
        helperText={error}
        inputProps={inputProps}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyPress={handleKeyPress}
      />
    </ReadOnlyFieldTooltip>
  );
};

InventoryTextField.propTypes = {
  value: PropTypes.string,
  label: PropTypes.node.isRequired,
  readOnly: PropTypes.bool,
  readOnlyTooltip: PropTypes.string,
  placeholder: PropTypes.string,
  normalize: PropTypes.func,
  validate: PropTypes.func,
  onCommit: PropTypes.func.isRequired,
  inputProps: PropTypes.shape({}),
};

InventoryTextField.defaultProps = {
  value: '',
  readOnly: false,
  readOnlyTooltip: null,
  placeholder: undefined,
  normalize: undefined,
  validate: undefined,
  inputProps: undefined,
};

export default memo(InventoryTextField);
